/**
 * Hardcoded functionality for managing data in Snowflake
 * 
 */
import LoadingCard from "@components/card/LoadingCard.component";
import { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from "@pages/PageStructure.component";
import { SnowflakeTableOption, useAvailableSnowflakeTables, usePipelineNodes } from "@stores/data.store";
import { PropsWithChildren, useCallback, useMemo, useState, KeyboardEvent, useRef, useEffect } from "react";
import { Badge, Collapse, Offcanvas } from "react-bootstrap";
import styled from 'styled-components';
import { TableExplorer } from "@components/datatable/TableExplorer.component";
import { Link, useNavigate } from "react-router-dom";
import { summarizeNumber } from "@services/formatting.service";
import { useTenantRegistration } from "@services/tenant/tenant.service";
import { NodeList } from "@components/pipelineNodes/PipelineNodeList.component";
import SourceIcon from "@components/sources/SourceIcon.component";
import snowflakeIcon from '@assets/images/snowflake_logo.png';
import { DraftOnly, ProdOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import SnowflakeGrantScript from "@components/sql/DataTableAccess.component";
import TableName from "@components/snowflake/TableName.component";
import { getErrorMessage } from "@services/errors.service";
import Danger from "@components/statusIndicators/Danger.component";

interface Table {
    type: 'TABLE' | 'VIEW';
    name: string;
    totalRecords: number;
}

interface Schema {
    name: string;
    tables: Table[];
}
interface Database {
    name: string;
    schemas: Schema[];
}


const TreeNodeStyles = styled.div`
div.inner {
    padding-left: 2rem;
}

.toggle {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 30px;
    &:hover {
        background-color: var(--ct-light);
        cursor:pointer;
    }

    &.active {
        font-weight: bold;
    }
}
`
interface TreeNodeProps {
    name: string;
    active?: boolean;
    onClick?: () => void;
    multiMode?: boolean;
    isSelected?: boolean; // For checkbox state in multiMode
}

const TreeNode = ({children, name, onClick, active, multiMode = false, isSelected = false}: PropsWithChildren<TreeNodeProps>) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const clicked = useCallback(() => {
        if (!children) {
            return onClick && onClick();
        }
        setIsOpen(!isOpen);
    }, [isOpen, children, onClick]);

    const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onClick && onClick();
    }, [onClick]);

    const icon = useMemo(() => {
        if (!children) {
            return '';
        }
        if (isOpen) {
            return 'mdi mdi-menu-down';
        }
        return 'mdi mdi-menu-down mdi-rotate-270';
    }, [isOpen, children]);

    const renderName = () => {
        if (multiMode && !children) { // Only show checkbox for leaf nodes in multiMode
            return (
                <div className="d-flex align-items-center">
                    <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={isSelected}
                        onChange={handleCheckboxChange}
                    />
                    <span>{name}</span>
                </div>
            );
        }
        return name;
    };

    return (
        <TreeNodeStyles>
            <div className={`toggle ${active ? 'active': ''}`} onClick={clicked} title={name}>
                {icon && <i className={icon}/>} {renderName()}
            </div>
            <Collapse in={isOpen}>
                <div className="inner">{children}</div>
            </Collapse>
        </TreeNodeStyles>
    );
};

interface SnowflakeTableSelectorProps {
    onSelect: (tables: string[]) => void;
    selectedTables: string[];
    multiMode?: boolean;
    hideHelp?: boolean; 
    header?: string;
    showSelections?: boolean;
}

const getHierarchicallyOrganizedTables = (tables: SnowflakeTableOption[]) => {
    const databasesByName: { [key: string]: Database } = {};
    const schemasByName: { [key: string]: Schema } = {};

    tables.forEach((table) => {
        if (!databasesByName[table.database_name]) {
            databasesByName[table.database_name] = {
                name: table.database_name,
                schemas: []
            };
        }
        const db = databasesByName[table.database_name];
        const fullSchemaName = `${table.database_name}.${table.schema_name}`;
        if (!schemasByName[fullSchemaName]) {
            schemasByName[fullSchemaName] = {
                name: table.schema_name,
                tables: []
            };
            db.schemas.push(schemasByName[fullSchemaName]);
        }
        const schema = schemasByName[fullSchemaName];
        schema.tables.push({
            type: table.rows == undefined ? 'VIEW' : 'TABLE',
            name: table.name,
            totalRecords: table.rows || 0,
        });
    });
    
    // Now sort everything
    Object.values(databasesByName).forEach((db) => {
        db.schemas.sort((a, b) => a.name.localeCompare(b.name));
        db.schemas.forEach((schema) => {
            schema.tables.sort((a, b) => a.name.localeCompare(b.name));
        });
    });
    return Object.values(databasesByName).sort((a, b) => a.name.localeCompare(b.name));
}

export const SnowflakeTableSelector = (props: SnowflakeTableSelectorProps) => {
    const tables = useAvailableSnowflakeTables();
    const { multiMode = false, selectedTables } = props;

    const selectedTablesSet = useMemo(() => {
        return new Set(selectedTables);
    }, [selectedTables]);

    // For single mode, get the first selected table for active state calculation
    const firstSelectedTable = selectedTables[0] || '';

    const isDatabaseSelected = useCallback((db: string) => {
        const allSelectedDatabases = Array.from(selectedTablesSet).map((table) => table.split('.')[0]);
        return allSelectedDatabases.includes(db);
    }, [selectedTablesSet]);

    const isSchemaSelected = useCallback((db: string, schema: string) => {
        const allSelectedSchemas = Array.from(selectedTablesSet).map((table) => {
            const parts = table.split('.');
            return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : '';
        });
        return allSelectedSchemas.includes(`${db}.${schema}`);
    }, [selectedTablesSet]);


    const isTableSelected = useCallback((db: string, schema: string, table: string) => {
        return selectedTablesSet.has(`${db}.${schema}.${table}`);
    }, [selectedTablesSet]);
    

    const hierarchicallyOrganizedTables: Database[] = useMemo(() => {
        if (!tables.data) {
            return [];
        }
        return getHierarchicallyOrganizedTables(tables.data);
    }, [tables.dataUpdatedAt]);

    const [showDbTableHelp, setShowDbTableHelp] = useState(false);
    const tenantRegistration = useTenantRegistration();

    const handleTableSelection = (fullTableName: string) => {
        if (multiMode) {
            const isSelected = selectedTablesSet.has(fullTableName);
            
            if (isSelected) {
                // Remove from selection
                const newSelected = selectedTables.filter(table => table !== fullTableName);
                props.onSelect(newSelected);
            } else {
                // Add to selection
                const newSelected = [...selectedTables, fullTableName];
                props.onSelect(newSelected);
            }
        } else {
            // Single mode: replace selection with just this table
            props.onSelect([fullTableName]);
        }
    };

    return <>
        <Offcanvas placement="end" show={showDbTableHelp} onHide={() => setShowDbTableHelp(false)}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Database Table Access</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Pane>
                    <PaneContent>
                        {tenantRegistration.data && <SnowflakeGrantScript 
                            role={tenantRegistration.data!.connection_creds.role}
                            database={tenantRegistration.data!.connection_creds.database} />}
                    </PaneContent>
                </Pane>
            </Offcanvas.Body>
        </Offcanvas>
        {tables.isLoading && <LoadingCard/>}
        {!tables.isLoading && <div>
            <div className="d-flex mb-3 center-vertically">
                {props.header && <h3 className="me-3 mb-0 flex-1">{props.header}</h3>}
                <button className="btn btn-light me-1" onClick={(() => {
                    tables.refetch();
                })}>
                    <i className={`mdi mdi-refresh ${tables.isFetching ? 'mdi-spin' : ''}`}></i> Refresh Tables
                </button>
                {!props.hideHelp && <button className="btn btn-light" onClick={() => {
                    setShowDbTableHelp(true);
                }}>
                    <i className="mdi mdi-information"></i> Help
                </button>}
                {multiMode && selectedTablesSet.size > 0 && (
                    <button className="btn btn-outline-secondary ms-1" onClick={() => {
                        props.onSelect([]);
                    }}>
                        Clear All ({selectedTablesSet.size})
                    </button>
                )}
            </div>
            {props.showSelections && selectedTables.length > 0 && (
                <div className="mb-3">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {selectedTables.map((table) => (
                            <Badge key={table} bg="light" className="me-1" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <span>{table}</span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${table}`}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        marginLeft: '0.25rem',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        lineHeight: 1,
                                        padding: 0,
                                    }}
                                    onClick={() => handleTableSelection(table)}
                                >
                                    <i className="mdi mdi-close-thick"></i>
                                </button>
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            <div className="bg-light p-3 border">
            {hierarchicallyOrganizedTables.length == 0 && <div className="text-muted">No tables found.</div>}
            {hierarchicallyOrganizedTables.map((db) => {
                return <TreeNode key={db.name} name={db.name} active={isDatabaseSelected(db.name)}>
                    {db.schemas.map((schema) => {
                        return <TreeNode key={schema.name} name={schema.name} active={isSchemaSelected(db.name, schema.name)}>
                            {schema.tables.map((table) => {
                                const fullTableName = `${db.name}.${schema.name}.${table.name}`;
                                const isSelected = isTableSelected(db.name, schema.name, table.name);
                                
                                return <TreeNode 
                                    key={table.name}
                                    name={table.name}
                                    active={!multiMode && isSelected} 
                                    multiMode={multiMode}
                                    isSelected={isSelected}
                                    onClick={() => {
                                        handleTableSelection(fullTableName);
                                    }}
                                />
                            })}
                        </TreeNode>
                    })}
                </TreeNode>
            })}
            </div>
            
        </div>}
    </>
}

interface SnowflakeDataSourceViewerProps {
    tableName: string;
    onUseTable: () => void;
}


export const SnowflakeDataSourceViewer = (props: SnowflakeDataSourceViewerProps) => {
    const tables = useAvailableSnowflakeTables();
    const searchInput = useRef<HTMLInputElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const selectedDb = useMemo(() => {
        if (props.tableName) {
            return props.tableName.split('.')[0];
        }
        return '';
    }, [props.tableName]);

    const selectedSchema = useMemo(() => {
        if (props.tableName) {
            return props.tableName.split('.')[1];
        }
        return '';
    }, [props.tableName]);

    const selectedTable = useMemo(() => {
        if (props.tableName) {
            return props.tableName.split('.')[2];
        }
        return '';
    }, [props.tableName]);


    const hierarchicallyOrganizedTables: Database[] = useMemo(() => {
        if (!tables.data) {
            return [];
        }
        return getHierarchicallyOrganizedTables(tables.data);

    }, [tables.dataUpdatedAt]);

    const navigate = useNavigate();
    const showTable = useCallback((database: string, schema: string, table: string) => {
        navigate(`?table=${database}.${schema}.${table}`);
    }, []);

    const selectedTableInfo = useMemo(() => {
        if (!props.tableName) {
            return null;
        }
        const db = hierarchicallyOrganizedTables.find((db) => db.name == selectedDb);
        if (!db) {
            return null;
        }
        const schema = db.schemas.find((schema) => schema.name == selectedSchema);
        if (!schema) {
            return null;
        }
        return schema.tables.find((table) => table.name == selectedTable);
    }, [selectedDb, props.tableName, selectedSchema, selectedTable, hierarchicallyOrganizedTables]);

    const checkForSearchEnterKey = useCallback((evt: KeyboardEvent<HTMLInputElement>) => {
        if (evt.keyCode == 13) {
            setSearchQuery(searchInput.current?.value || '')
        }
    }, [searchInput, props.tableName]);

    const [loading, setLoading] = useState(false);

    return <>
        
        {props.tableName && <>
            <div style={{height: '80px'}} className="border-bottom">
                <div className="d-flex center-vertically mb-2">
                    <div className="overflow-ellipsis flex-grow-1">
                        <>
                            <TableName table_name={props.tableName} />
                            <div className="mb-1 font-poppins text-muted font-13">
                                <span>
                                    <Badge>{selectedTableInfo?.type}</Badge>
                                </span>
                                <span className="me-3">&nbsp;</span>
                                <span>
                                    <i className="mdi mdi-file-multiple"></i> Records: {selectedTableInfo ? summarizeNumber(selectedTableInfo.totalRecords) : '0'}
                                </span>
                            </div>
                        </>
                    </div>
                    <div className="">
                    
                        <input type="text" className=" form-control d-inline-block input-rounded me-2" placeholder="Search (free-text or lucene)" defaultValue={searchQuery} ref={searchInput} onKeyDown={checkForSearchEnterKey} style={{width: '300px', marginLeft: '10px' }}/>
                    
                    </div>
                </div>
                
            </div>
            <div style={{height: 'calc(100% - 80px)'}}>
                <TableExplorer
                    tablePath={`/db/data?table="${selectedDb}"."${selectedSchema}"."${selectedTable}"`}
                    searchQuery={searchQuery}
                />
            </div>
        </>}
                        
                
        
    </>
}

const ensureDoubleQuotes = (str: string) => {
    if (str.indexOf('"') == -1) {
        return `"${str}"`;
    }
    return str;
}

const SnowflakeDataSourcePage = () => {
    const pipelineNodes = usePipelineNodes();
    const isLoading = pipelineNodes.isLoading;

    const tableNodes = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }

        return pipelineNodes.data.filter((node) => node.node_type == 'SOURCE' && !node.flat_file).sort((a, b) => a.label.localeCompare(b.label));
    }, [pipelineNodes.dataUpdatedAt]);

    const [timesOnSnowflakePage, setTimesOnSnowflakePage] = useState(0);

    useEffect(() => {
        const currentTimes = localStorage.getItem('timesOnSnowflakePage');
        const parsed = currentTimes ? parseInt(currentTimes, 10) : 0; 
        if (currentTimes) {
            setTimesOnSnowflakePage(parsed);
        } else {
            setTimesOnSnowflakePage(0);
        }

        localStorage.setItem('timesOnSnowflakePage', ((parsed || 0) + 1).toString());
    }, []);

    return <PageContent>
         <PageContentHeader>
            <div>
                <SourceIcon image_url={snowflakeIcon} />
                <div className="flex-1 ms-2">
                    <h1 className="mb-0">Snowflake Tables</h1>
                    <div className="text-muted font-13">
                        View all Snowflake tables you have connected to Pliable.
                    </div>

                </div>
                <DraftOnly>
                    <Link className="btn btn-primary" to={`/wizard/data-source?sourceType=snowflake`}>
                        <i className="mdi mdi-connection"></i> Connect New
                    </Link>
                </DraftOnly>
            </div>
        </PageContentHeader>
        <PageContentInner hasHeader>
        <div className="p-4">
        <div className="card">
            <div className="card-body">
                {tableNodes.length == 0 && !isLoading && <div>
                    <DraftOnly>

                        <div className="empty-state">
                            <div className="text-center">
                                <h3>You haven't connected any tables yet.</h3>
                                <p>Pliable can pull data directly from any table in your Snowflake warehouse.</p>
                                <ReadOnly>
                                    <Link className="btn btn-primary" to={`/wizard/data-source?sourceType=snowflake`}>Connect one now.</Link>
                                </ReadOnly>
                            
                            </div>
                        </div>
                    </DraftOnly>
                    <ProdOnly>
                        <p>You are currently in <strong>Production Mode.</strong> To connect tables, please enter Develop Mode.</p>

                    </ProdOnly>
                </div>}
                <NodeList nodes={tableNodes} />
            </div>
        </div>
        
       
    </div>
        </PageContentInner>
        </PageContent>
}

export default SnowflakeDataSourcePage;

